<template>
  <div class="smaller-container-wrapper full-height no-progress-bar">
    <h2 class="pt-3 pt-sm-4 mb-0 mb-sm-4 pb-sm-2">{{ $t('message.eCommerceV2.offer.offerSummaryForm.heading') }}</h2>
    <hr class="mt-3 mb-4 mt-sm-4" />
    <h3 class="h4 pt-2">{{ vehicleString }}</h3>
    <ul class="list-unstyled my-4">
      <template v-if="paymentMethod === this.enums.PaymentMethod.CASH">
        <li class="mb-3 mb-sm-4">
          <span class="primary-fill-svg">
            <inline-svg class="mr-2 mb-1" :src="require('@/assets/check-circle-blue-fill.svg')" alt="" />
          </span>
          {{ $t('message.eCommerceV2.offer.offerSummaryForm.summaryList1Cash') }}
        </li>
        <li class="mb-3 mb-sm-4">
          <span class="primary-fill-svg">
            <inline-svg class="mr-2 mb-1" :src="require('@/assets/check-circle-blue-fill.svg')" alt="" />
          </span>
          {{
            $t('message.eCommerceV2.offer.offerSummaryForm.summaryList2Cash', {
              amount: $n(paymentAmount, 'currencyDollars', languageCode),
            })
          }}
        </li>
      </template>
      <template v-else>
        <li class="mb-3 mb-sm-4">
          <span class="primary-fill-svg">
            <inline-svg class="mr-2 mb-1" :src="require('@/assets/check-circle-blue-fill.svg')" alt="" />
          </span>
          {{
            $t('message.eCommerceV2.offer.offerSummaryForm.summaryList1', {
              terms: `${paymentTerm} ${$t('message.eCommerceV2.paymentCalculator.months')}`,
              period: $t(`message.eCommerceV2.paymentCalculator.${paymentPeriod}PeriodShort`),
              paymentAmount: $n(paymentAmount, 'currencyDollars', languageCode),
              leaseOrBuy,
            })
          }}
        </li>
        <li class="mb-3 mb-sm-4">
          <span class="primary-fill-svg">
            <inline-svg class="mr-2 mb-1" :src="require('@/assets/check-circle-blue-fill.svg')" alt="" />
          </span>
          {{
            $t('message.eCommerceV2.offer.offerSummaryForm.summaryList2', {
              downPayment: $n(downPayment || 0, 'currencyDollars', languageCode),
            })
          }}
        </li>
      </template>
      <li v-if="tradeInValue" class="mb-3 mb-sm-4">
        <span class="primary-fill-svg">
          <inline-svg class="mr-2 mb-1" :src="require('@/assets/check-circle-blue-fill.svg')" alt="" />
        </span>
        {{
          $t('message.eCommerceV2.offer.offerSummaryForm.summaryList3', {
            tradeValue: $n(tradeInValue, 'currencyDollars', languageCode),
          })
        }}
        <span v-if="outstandingTradeInBalance">
          {{
            $t('message.eCommerceV2.offer.offerSummaryForm.summaryList3b', {
              outstandingBalance: $n(outstandingTradeInBalance, 'currencyDollars', languageCode),
            })
          }}
        </span>
      </li>
    </ul>
    <p class="pb-3 small pt-2">
      <template v-if="paymentMethod === this.enums.PaymentMethod.CASH">{{
        $t('message.eCommerceV2.offer.offerSummaryForm.cashMsg')
      }}</template>
      <template v-else>{{ $t('message.eCommerceV2.offer.offerSummaryForm.msg') }}</template>
    </p>
    <template v-if="showForm">
      <hr class="my-4" />
      <contact-form
        ref="contactForm"
        @contactFormSuccessfullySubmitted="createLead"
        :button-text="$t('message.eCommerceV2.offer.offerSummaryForm.btnText')"
      ></contact-form>
    </template>
    <div class="push-down-desktop mt-3">
      <mobialis-button class="mb-3" @click="submit()" data-cy="sendDealer">{{
        $t('message.eCommerceV2.offer.offerSummaryForm.btnText')
      }}</mobialis-button>
      <mobialis-button
        type="button"
        variant="secondary"
        @click="$router.push('../payment-calculator')"
        data-cy="backToDeal"
        v-av-analytics-cta-interaction="{
          elementText: $t('message.eCommerceV2.offer.offerSummaryForm.btnText2'),
          eventActionResult: 'open',
        }"
        >{{ $t('message.eCommerceV2.offer.offerSummaryForm.btnText2') }}</mobialis-button
      >
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import log from '@/services/log';
import ContactForm from '@/components/shared/ContactForm.vue';
import { AVAnalyticsEventTriggers } from '@/utils/enums';
import helperFunctions from '@/utils/helperFunctions';

export default {
  name: 'OfferSummaryForm',
  components: {
    ContactForm,
  },
  data() {
    return {};
  },
  computed: {
    ...mapGetters({
      vehicleString: 'vehicle/vehicleString',
      languageCode: 'user/getLanguageCodeForNumericalInternationalization',
      creditScoreGrade: 'credit/creditScoreGrade',
      regionalCreditScoreGrade: 'credit/regionalCreditScoreGrade',
      businessName: 'business/getBusinessName',
      paymentAmount: 'paymentCalculator/currentPaymentAmount',
      paymentTerm: 'paymentCalculator/currentPaymentTerm',
    }),
    ...mapState('paymentCalculator', [
      'tradeInValue',
      'outstandingTradeInBalance',
      'paymentPeriod',
      'paymentMethod',
      'downPayment',
    ]),
    leaseOrBuy() {
      return this.paymentMethod === this.enums.PaymentMethod.LEASE
        ? this.$t('message.eCommerceV2.offer.offerSummaryForm.lease')
        : this.$t('message.eCommerceV2.offer.offerSummaryForm.buy');
    },
    showForm() {
      return !this.contactFormInfoComplete || !this.$store.getters['lead/leadIsActive'];
    },
  },
  mounted() {
    if (!this.showForm) {
      helperFunctions.sendAVAnalyticsEvent(this.$route.path, AVAnalyticsEventTriggers.INTERACTION, this.$store);
    }
  },
  methods: {
    async submit() {
      if (this.showForm) {
        this.$refs.contactForm.submitContactForm();
      } else {
        this.$store.commit('SET_LOADING', true);
        await this.createOffer();
      }
    },
    // Try to create lead, we may or may not have one yet
    async createLead() {
      try {
        await this.$store.dispatch('lead/createLead', { subType: this.enums.SubType.ECOMV2 });
        await this.createOffer();
      } catch (err) {
        log('Error in ecomv2 createLead', err);
        this.$router.push('error');
        this.$store.commit('SET_LOADING', false);
      }
    },
    async createOffer() {
      try {
        await this.$store.dispatch('lead/createOffer');
        // If we don't have a credit score then get one
        // just more info for the dealer
        if (!this.creditScoreGrade && !this.regionalCreditScoreGrade) {
          await this.getRegionalCredit();
        } else {
          this.$router.push({ path: this.getNextRoutePath() });
          this.$store.commit('SET_LOADING', false);
        }
      } catch (err) {
        log('Error in ecomv2 createOffer', err);
        this.$router.push('error');
        this.$store.commit('SET_LOADING', false);
      }
    },
    async getRegionalCredit() {
      try {
        await this.$store.dispatch('credit/getRegionalCreditScore');
      } catch (err) {
        // No need to go to error page, just log error
        log('Error in ecomv2 getRegionalCreditScore on offer summary form', err);
      } finally {
        this.$router.push({ path: this.getNextRoutePath() });
        this.$store.commit('SET_LOADING', false);
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
